// Sales Page Builder — assembles existing report data into a Shopify-ready sales page structure

export interface SalesPageOutput {
  heroHeadlines: string[];
  heroSubhead: string;
  benefitBullets: string[];
  whyThisWhyNow: string;
  howItWorks: string[];
  socialProof: string[];
  faq: { q: string; a: string }[];
  offerStack: string[];
  guaranteeSuggestion: string;
  ctaOptions: string[];
  whoItsFor: string[];
  whoItsNotFor: string[];
  disclaimer?: string;
}

const HEALTH_KEYWORDS = /\b(health|supplement|vitamin|wellness|nutrition|diet|medical|pharma|herbal|probiotic|collagen|cbd|sleep aid|pain relief)\b/i;

/** Strip personal identifiers from evidence quotes */
export function sanitizeQuote(text: string): string {
  return text
    .replace(/@[\w.-]+/g, "")                          // @usernames
    .replace(/https?:\/\/\S+/g, "")                    // URLs
    .replace(/\b[A-Z][a-z]+ [A-Z][a-z]+\b/g, "")      // "First Last" names
    .replace(/\s{2,}/g, " ")
    .trim();
}

export function buildSalesPage(
  report: any,
  themes: any[],
  objections: any[],
  outcomes: string[],
  sellerClaims: string[],
  pack: any,
): SalesPageOutput {
  const titles: string[] = pack?.titles || [];
  const bullets: string[] = pack?.bullets || [];
  const adHooks: string[] = pack?.ad_hooks || [];
  const faqs: { q: string; a: string }[] = pack?.faqs || [];
  const summary: string = pack?.summary || "";
  const description: string = pack?.description || "";
  const industry: string = report?.industry_context || report?.product_category || "";

  // ── Hero Headlines (3) — trimmed to max 10 words ──
  const trimTo10 = (s: string) => s.split(/\s+/).slice(0, 10).join(" ");
  const heroHeadlines: string[] = [];
  if (adHooks[0]) heroHeadlines.push(trimTo10(adHooks[0]));
  if (titles[0]) heroHeadlines.push(trimTo10(titles[0]));
  if (themes[0]?.messaging_angle) heroHeadlines.push(trimTo10(themes[0].messaging_angle));
  if (titles[1] && heroHeadlines.length < 3) heroHeadlines.push(trimTo10(titles[1]));
  if (adHooks[1] && heroHeadlines.length < 3) heroHeadlines.push(trimTo10(adHooks[1]));
  while (heroHeadlines.length < 3 && titles.length > heroHeadlines.length) {
    heroHeadlines.push(trimTo10(titles[heroHeadlines.length]));
  }

  // ── Hero Subhead ──
  const firstSentence = summary.split(/(?<=[.!?])\s+/)[0] || summary.slice(0, 120);
  const heroSubhead = firstSentence;

  // ── Benefit Bullets (6, max 12 words each) ──
  const trimTo12 = (s: string) => s.split(/\s+/).slice(0, 12).join(" ");
  const benefitBullets = [...bullets.slice(0, 4), ...outcomes.slice(0, 4)].slice(0, 6).map(trimTo12);

  // ── Why This / Why Now ──
  const messagingAngles = themes.slice(0, 3).map(t => t.messaging_angle).filter(Boolean);
  const whyThisWhyNow = [
    summary.split(/(?<=[.!?])\s+/).slice(0, 3).join(" "),
    messagingAngles[0] || "",
  ].filter(Boolean).join("\n\n");

  // ── How It Works (3-5 steps from themes) ──
  const howItWorks = themes.slice(0, 5).map((t, i) => {
    const verb = t.title || `Step ${i + 1}`;
    const detail = t.bullets?.[0] || "";
    return `${i + 1}. ${verb}${detail ? ` — ${detail}` : ""}`;
  });

  // ── Social Proof (3 anonymised quotes) ──
  const allQuotes: string[] = [];
  for (const t of themes) {
    if (t.bullets) {
      for (const b of t.bullets) {
        const cleaned = sanitizeQuote(b);
        if (cleaned.length > 20 && !allQuotes.includes(cleaned)) {
          allQuotes.push(cleaned);
        }
        if (allQuotes.length >= 3) break;
      }
    }
    if (allQuotes.length >= 3) break;
  }
  const socialProof = allQuotes.slice(0, 3).map(q => `"${q}"`);

  // ── FAQ (6) from objections ──
  const faq: { q: string; a: string }[] = objections.slice(0, 6).map((o: any) => ({
    q: o.objection || o.q || "",
    a: o.reassurance || o.a || "",
  }));
  // Fill remaining from pack FAQs
  if (faq.length < 6 && faqs.length) {
    for (const f of faqs) {
      if (faq.length >= 6) break;
      if (!faq.some(existing => existing.q === f.q)) {
        faq.push({ q: f.q, a: f.a });
      }
    }
  }

  // ── Offer Stack ──
  const offerStack = [
    ...bullets.slice(0, 3).map(b => `✓ ${b}`),
    description ? `✓ ${description.split(/(?<=[.!?])\s+/)[0]}` : "",
  ].filter(Boolean);

  // ── Guarantee ──
  const productName = report?.topic || report?.product_category || "this product";
  const guaranteeSuggestion = `Try ${productName} risk-free. If you're not completely satisfied, we'll make it right — no questions asked.`;

  // ── CTA Options (3) ──
  const ctaOptions: string[] = [];
  if (adHooks[0]) ctaOptions.push(`Get ${productName} Now`);
  if (themes[0]?.title) ctaOptions.push(`Start Seeing Results Today`);
  ctaOptions.push(`Shop ${productName}`);
  while (ctaOptions.length > 3) ctaOptions.pop();

  // ── Who It's For ──
  const whoItsFor = outcomes.slice(0, 5).map(o => `People who want: ${o}`);

  // ── Who It's Not For ──
  const whoItsNotFor = objections.slice(0, 3).map((o: any) => {
    const concern = o.objection || "";
    return `Not ideal if: ${concern}`;
  });

  // ── Disclaimer ──
  let disclaimer: string | undefined;
  if (HEALTH_KEYWORDS.test(industry)) {
    disclaimer = "⚠️ Not medical advice. These statements have not been evaluated by any medical authority. This product is not intended to diagnose, treat, cure, or prevent any disease. Consult a healthcare professional before use.";
  }

  return {
    heroHeadlines,
    heroSubhead,
    benefitBullets,
    whyThisWhyNow,
    howItWorks,
    socialProof,
    faq,
    offerStack,
    guaranteeSuggestion,
    ctaOptions,
    whoItsFor,
    whoItsNotFor,
    disclaimer,
  };
}

/** Assemble a single plain-text sales page for clipboard */
export function buildSalesPageText(sp: SalesPageOutput): string {
  const lines: string[] = [];

  lines.push("═══ SALES PAGE (SHOPIFY-READY) ═══");
  lines.push("");

  lines.push("HERO HEADLINES");
  sp.heroHeadlines.forEach((h, i) => lines.push(`  ${i + 1}. ${h}`));
  if (sp.heroSubhead) { lines.push(""); lines.push(`Subhead: ${sp.heroSubhead}`); }
  lines.push("");

  lines.push("BENEFIT BULLETS");
  sp.benefitBullets.forEach(b => lines.push(`  • ${b}`));
  lines.push("");

  lines.push("WHY THIS / WHY NOW");
  lines.push(sp.whyThisWhyNow);
  lines.push("");

  lines.push("HOW IT WORKS");
  sp.howItWorks.forEach(s => lines.push(`  ${s}`));
  lines.push("");

  if (sp.socialProof.length) {
    lines.push("SOCIAL PROOF");
    sp.socialProof.forEach(q => lines.push(`  ${q}`));
    lines.push("");
  }

  lines.push("FAQ");
  sp.faq.forEach(f => { lines.push(`  Q: ${f.q}`); lines.push(`  A: ${f.a}`); lines.push(""); });

  lines.push("OFFER STACK");
  sp.offerStack.forEach(item => lines.push(`  ${item}`));
  lines.push("");
  lines.push(`GUARANTEE: ${sp.guaranteeSuggestion}`);
  lines.push("");

  lines.push("CTA OPTIONS");
  sp.ctaOptions.forEach((c, i) => lines.push(`  ${i + 1}. ${c}`));
  lines.push("");

  lines.push("WHO IT'S FOR");
  sp.whoItsFor.forEach(w => lines.push(`  • ${w}`));
  lines.push("");

  lines.push("WHO IT'S NOT FOR");
  sp.whoItsNotFor.forEach(w => lines.push(`  • ${w}`));

  if (sp.disclaimer) {
    lines.push("");
    lines.push(`DISCLAIMER: ${sp.disclaimer}`);
  }

  return lines.join("\n");
}
